import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { playClick, playToggle, playSuccess } from '@/utils/sounds';
import Baseball from '@/components/Baseball';
import RadioTower from '@/components/RadioTower';

const API = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

/**
 * Phone Controller for TV Mode
 * 
 * Matches the web app aesthetic and gameplay flow exactly.
 * Shows answers to tap (not type), cream background, pixel styling.
 */
export default function ControllerPage() {
  const { code: urlCode } = useParams();
  const [searchParams] = useSearchParams();
  const codeFromQuery = searchParams.get('code');
  
  const [sessionCode, setSessionCode] = useState(urlCode || codeFromQuery || '');
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [tvConnected, setTvConnected] = useState(false);
  const [gameState, setGameState] = useState(null);
  const [currentSong, setCurrentSong] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState(null);
  
  // Timer for pitch progress
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [playProgress, setPlayProgress] = useState(0);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  
  // Guesses - tap to toggle (matches web app)
  const [guesses, setGuesses] = useState({ title: false, artist: false, album: false, year: false });
  
  // Team setup
  const [teamA, setTeamA] = useState({ name: 'Team 1', players: ['Player 1'] });
  const [teamB, setTeamB] = useState({ name: 'Team 2', players: ['Player 1'] });
  
  // WebSocket
  const wsRef = useRef(null);
  const connectRef = useRef(null);
  const hasAttemptedAutoConnectRef = useRef(false);

  // Start pitch timer (30 seconds)
  const startPitchTimer = useCallback(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    const duration = 30000; // 30 seconds
    startTimeRef.current = Date.now();
    setTimeRemaining(30);
    setPlayProgress(0);
    
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min((elapsed / duration) * 100, 100);
      const remaining = Math.max(Math.ceil((duration - elapsed) / 1000), 0);
      
      setPlayProgress(progress);
      setTimeRemaining(remaining);
      
      if (elapsed >= duration) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 100);
  }, []);

  // Stop pitch timer
  const stopPitchTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTimeRemaining(30);
    setPlayProgress(0);
  }, []);

  // Connect to session
  const connectToSession = useCallback(async (code) => {
    if (!code || code.length < 6) {
      toast.error('Please enter a valid 6-character code');
      return;
    }

    // Prevent double-connection attempts
    if (connecting || connected) {
      console.log('Already connecting or connected, skipping');
      return;
    }

    setConnecting(true);
    console.log('Connecting to session:', code);

    try {
      // First verify session exists via REST
      const joinUrl = `${API}/api/sessions/join/${code.toUpperCase()}`;
      console.log('Joining via:', joinUrl);
      
      const joinRes = await fetch(joinUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      console.log('Join response status:', joinRes.status);

      if (!joinRes.ok) {
        let errorMsg = 'Session not found';
        try {
          const err = await joinRes.json();
          errorMsg = err.detail || errorMsg;
        } catch (e) {
          // Response wasn't JSON
        }
        console.error('Join error:', errorMsg);
        toast.error(errorMsg);
        setConnecting(false);
        return;
      }

      const data = await joinRes.json();
      console.log('Join successful:', data);
      setGameState(data.state);

      // Connect WebSocket
      const wsUrl = `${WS_URL}/sessions/${code.toUpperCase()}`;
      console.log('Connecting WebSocket to:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket opened, sending role');
        ws.send(JSON.stringify({ role: 'controller' }));
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        console.log('Controller received:', message);

        switch (message.event) {
          case 'connected':
            setConnected(true);
            setConnecting(false);
            setGameState(message.state);
            setTvConnected(true);
            toast.success('Connected to game!');
            break;
            
          case 'tv_connected':
            setTvConnected(true);
            break;
            
          case 'tv_disconnected':
            setTvConnected(false);
            toast.warning('TV disconnected');
            break;
            
          case 'state_update':
          case 'game_started':
          case 'inning_changed':
            setGameState(message.state);
            setLastResult(null);
            setCurrentSong(null);
            setCorrectAnswers(null);
            setGuesses({ title: false, artist: false, album: false, year: false });
            break;
            
          case 'pitch_started':
            setGameState(message.state);
            setCurrentSong(message.song); // Controller gets full song with answers
            setLastResult(null);
            setCorrectAnswers(null);
            setGuesses({ title: false, artist: false, album: false, year: false });
            playSuccess();
            // Start the 30-second timer
            startPitchTimer();
            break;
            
          case 'answer_submitted':
            setGameState(message.state);
            setLastResult(message);
            setCorrectAnswers(message.correct_answers);
            setCurrentSong(null);
            // Stop the timer when answer is submitted
            stopPitchTimer();
            break;
            
          case 'error':
            toast.error(message.message);
            break;
            
          default:
            if (message.state) {
              setGameState(message.state);
            }
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        toast.error('Connection error - please try again');
        setConnected(false);
        setConnecting(false);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setConnected(false);
        setConnecting(false);
        // Show disconnect message if we were previously connected
        if (connected) {
          toast.error('Disconnected from game');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Connection error:', err);
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        toast.error('Network error - check your connection');
      } else {
        toast.error('Failed to connect - please try again');
      }
      setConnecting(false);
    }
  }, [connecting, connected]);

  // Keep ref updated with latest connectToSession
  useEffect(() => {
    connectRef.current = connectToSession;
  }, [connectToSession]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Auto-connect if code in URL (ONLY ONCE on initial mount)
  useEffect(() => {
    const code = urlCode || codeFromQuery;
    if (code && code.length >= 6 && !connected && !connecting && !hasAttemptedAutoConnectRef.current) {
      hasAttemptedAutoConnectRef.current = true;
      // Longer delay to ensure session is ready on the backend
      const timer = setTimeout(() => {
        if (connectRef.current) {
          connectRef.current(code);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [urlCode, codeFromQuery, connected, connecting]);

  // Send action to server
  const sendAction = (action, payload = {}) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, payload }));
    }
  };

  // Toggle a guess (tap to select/deselect)
  const toggleGuess = (field) => {
    playToggle();
    setGuesses(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Submit the current guesses
  const handleEndPitch = () => {
    playClick();
    const correctCount = Object.values(guesses).filter(Boolean).length;
    
    // Build answers object based on what was toggled correct
    const answers = {
      title: guesses.title ? currentSong.title : '',
      artist: guesses.artist ? currentSong.artist : '',
      album: guesses.album ? currentSong.album : '',
      year: guesses.year ? String(currentSong.year) : ''
    };
    
    sendAction('submit_answer', { answers });
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Get result display
  const getResultEmoji = (result) => {
    switch (result) {
      case 'SINGLE': return '1B';
      case 'DOUBLE': return '2B';
      case 'TRIPLE': return '3B';
      case 'HOME_RUN': return 'HR';
      case 'OUT': return 'OUT';
      default: return '';
    }
  };

  // ==================== SCREENS ====================

  // Not connected - show join screen
  if (!connected) {
    return (
      <div className="min-h-screen bg-cream p-4 flex flex-col items-center justify-center">
        <h1 className="retro-heading text-2xl text-grass mb-8">JOIN GAME</h1>
        
        <div className="pixel-card w-full max-w-sm p-6">
          <label className="retro-body text-sm text-dirt block mb-2">ENTER CODE</label>
          <input
            type="text"
            value={sessionCode}
            onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
            className="pixel-input w-full text-2xl text-center tracking-widest mb-4"
            placeholder="ABC123"
            maxLength={6}
            disabled={connecting}
            data-testid="session-code-input"
          />
          
          <button
            onClick={() => { playClick(); connectToSession(sessionCode); }}
            className="pixel-btn pixel-btn-primary w-full"
            disabled={connecting}
            data-testid="join-btn"
          >
            {connecting ? 'CONNECTING...' : 'JOIN'}
          </button>
          
          {connecting && (
            <p className="retro-body text-xs text-dirt text-center mt-3">
              Please wait...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Setup phase - show team configuration
  if (gameState?.phase === 'setup') {
    return (
      <div className="min-h-screen bg-cream p-4">
        <h1 className="retro-heading text-xl text-grass text-center mb-4">SETUP TEAMS</h1>
        
        <div className="space-y-4 max-w-sm mx-auto">
          {/* Team A */}
          <div className="pixel-card p-4">
            <label className="retro-body text-xs text-dirt">TEAM 1 NAME</label>
            <input
              type="text"
              value={teamA.name}
              onChange={(e) => setTeamA({ ...teamA, name: e.target.value })}
              className="pixel-input w-full mt-1"
              data-testid="team-a-name"
            />
            <label className="retro-body text-xs text-dirt mt-3 block">PLAYERS (comma separated)</label>
            <input
              type="text"
              value={teamA.players.join(', ')}
              onChange={(e) => setTeamA({ ...teamA, players: e.target.value.split(',').map(p => p.trim()).filter(Boolean) })}
              className="pixel-input w-full mt-1"
              data-testid="team-a-players"
            />
          </div>

          {/* Team B */}
          <div className="pixel-card p-4">
            <label className="retro-body text-xs text-dirt">TEAM 2 NAME</label>
            <input
              type="text"
              value={teamB.name}
              onChange={(e) => setTeamB({ ...teamB, name: e.target.value })}
              className="pixel-input w-full mt-1"
              data-testid="team-b-name"
            />
            <label className="retro-body text-xs text-dirt mt-3 block">PLAYERS (comma separated)</label>
            <input
              type="text"
              value={teamB.players.join(', ')}
              onChange={(e) => setTeamB({ ...teamB, players: e.target.value.split(',').map(p => p.trim()).filter(Boolean) })}
              className="pixel-input w-full mt-1"
              data-testid="team-b-players"
            />
          </div>

          <button
            onClick={() => {
              playClick();
              sendAction('set_teams', { team_a: teamA, team_b: teamB });
              sendAction('start_game', {});
            }}
            className="pixel-btn pixel-btn-primary w-full"
            data-testid="start-game-btn"
          >
            START GAME
          </button>
        </div>
      </div>
    );
  }

  // Game over
  if (gameState?.phase === 'game_over') {
    const winner = gameState.team_a.score > gameState.team_b.score 
      ? gameState.team_a.name 
      : gameState.team_b.score > gameState.team_a.score
        ? gameState.team_b.name
        : 'TIE';

    return (
      <div className="min-h-screen bg-cream p-4 flex flex-col items-center justify-center text-center">
        <h1 className="retro-heading text-2xl text-dirt mb-4">GAME OVER</h1>
        
        <div className="pixel-card p-6 mb-4">
          <p className="retro-heading text-lg text-ink">
            {gameState.team_a.name}: {gameState.team_a.score}
          </p>
          <p className="retro-heading text-lg text-ink">
            {gameState.team_b.name}: {gameState.team_b.score}
          </p>
        </div>
        
        <div className="pixel-card p-4 bg-grass">
          <p className="retro-body text-sm text-cream">WINNER</p>
          <p className="retro-heading text-xl text-cream">
            {winner === 'TIE' ? "IT'S A TIE!" : winner}
          </p>
        </div>
      </div>
    );
  }

  // Result phase - show result and next action
  if (lastResult && (gameState?.phase === 'result' || gameState?.phase === 'inning_summary')) {
    return (
      <div className="min-h-screen bg-cream p-4 flex flex-col items-center justify-center text-center">
        <div className="pixel-card p-4 mb-4 relative z-10" data-testid="result-phase">
          <h2 className="retro-heading text-xs text-ink mb-2">RESULT</h2>
          
          <div className={`led-display text-3xl mb-4 ${
            lastResult.result === 'OUT' ? 'text-red-500' : ''
          }`}>
            {getResultEmoji(lastResult.result)}
          </div>
          
          <p className="retro-heading text-base text-ink mb-2">
            {lastResult.result.replace('_', ' ')}
          </p>
          
          <p className="retro-body text-base text-dirt mb-2">
            {lastResult.correct_count}/4 correct
          </p>

          {lastResult.runs_scored > 0 && (
            <p className="retro-body text-lg text-grass mb-3">
              +{lastResult.runs_scored} RUN{lastResult.runs_scored > 1 ? 'S' : ''}!
            </p>
          )}
          
          <button
            onClick={() => {
              playClick();
              if (gameState.outs >= 3 || gameState.phase === 'inning_summary') {
                sendAction('next_inning', {});
              } else {
                sendAction('next_batter', {});
              }
            }}
            className="pixel-btn pixel-btn-primary text-xs"
            data-testid="next-btn"
          >
            {gameState.outs >= 3 || gameState.phase === 'inning_summary' ? 'END OF INNING' : 'NEXT BATTER'}
          </button>
        </div>
      </div>
    );
  }

  // Get current team info
  const isTopInning = gameState?.half === 'top';
  const battingTeam = isTopInning ? gameState?.team_a : gameState?.team_b;
  const pitchingTeam = isTopInning ? gameState?.team_b : gameState?.team_a;
  const currentBatter = battingTeam?.players?.[gameState?.current_batter_index] || 'Player';

  // Pitching phase with song - show answers to tap
  if (currentSong && gameState?.phase === 'pitching') {
    return (
      <div className="min-h-screen bg-cream p-4">
        {/* Header with pitcher info - matching GamePlay.js */}
        <div className="pixel-card p-3 mb-4" data-testid="playing-phase">
          {/* Header with pitcher info and timer */}
          <div className="flex items-center justify-between mb-2 pb-2 border-b-2 border-dirt/30">
            <div>
              <p className="retro-body text-xs text-dirt">{pitchingTeam?.name} PITCHING</p>
              <p className="retro-heading text-sm text-grass">{currentBatter} batting</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="retro-heading text-lg text-ink">{timeRemaining}s</span>
              <span className="retro-heading text-xl text-ink">
                {isTopInning ? '▲' : '▼'} {gameState.inning}
              </span>
            </div>
          </div>
          
          {/* Audio Playhead - visual indicator matching GamePlay.js */}
          <div className="audio-playhead mb-3 h-4" data-testid="audio-playhead">
            <div className="audio-playhead-fill h-full" style={{ width: `${playProgress}%` }}></div>
          </div>

          {/* Instruction */}
          <p className="retro-body text-xs text-center text-dirt mb-2">
            {timeRemaining > 0 
              ? `Tap answers ${currentBatter} gets correct:`
              : `Audio ended - select answers then END PITCH`
            }
          </p>

          {/* Answer Buttons - matching GamePlay.js exactly */}
          <div className="space-y-2 mb-3">
            <div 
              className={`p-2 border-4 cursor-pointer transition-all ${
                guesses.title 
                  ? 'bg-grass border-grass text-white' 
                  : 'bg-white border-black text-ink active:bg-gray-100'
              }`}
              onClick={() => toggleGuess('title')}
              data-testid="guess-title"
            >
              <span className="retro-body text-xs block opacity-75">TITLE</span>
              <span className="retro-body text-base leading-tight">{currentSong.title}</span>
            </div>
            
            <div 
              className={`p-2 border-4 cursor-pointer transition-all ${
                guesses.artist 
                  ? 'bg-grass border-grass text-white' 
                  : 'bg-white border-black text-ink active:bg-gray-100'
              }`}
              onClick={() => toggleGuess('artist')}
              data-testid="guess-artist"
            >
              <span className="retro-body text-xs block opacity-75">ARTIST</span>
              <span className="retro-body text-base leading-tight">{currentSong.artist}</span>
            </div>
            
            <div 
              className={`p-2 border-4 cursor-pointer transition-all ${
                guesses.album 
                  ? 'bg-grass border-grass text-white' 
                  : 'bg-white border-black text-ink active:bg-gray-100'
              }`}
              onClick={() => toggleGuess('album')}
              data-testid="guess-album"
            >
              <span className="retro-body text-xs block opacity-75">ALBUM</span>
              <span className="retro-body text-base leading-tight">{currentSong.album}</span>
            </div>
            
            <div 
              className={`p-2 border-4 cursor-pointer transition-all ${
                guesses.year 
                  ? 'bg-grass border-grass text-white' 
                  : 'bg-white border-black text-ink active:bg-gray-100'
              }`}
              onClick={() => toggleGuess('year')}
              data-testid="guess-year"
            >
              <span className="retro-body text-xs block opacity-75">YEAR</span>
              <span className="retro-body text-base">{currentSong.year}</span>
            </div>
          </div>

          {/* End Pitch Button - matching GamePlay.js */}
          <button
            onClick={handleEndPitch}
            className="pixel-btn pixel-btn-primary w-full py-2 text-xs"
            data-testid="end-pitch-btn"
          >
            END PITCH
          </button>
        </div>

        {/* Score display */}
        <div className="text-center">
          <p className="retro-body text-sm text-dirt">
            {gameState.team_a.name}: {gameState.team_a.score} | {gameState.team_b.name}: {gameState.team_b.score}
          </p>
        </div>
      </div>
    );
  }

  // Default state - waiting for pitch or ready to pitch
  return (
    <div className="min-h-screen bg-cream p-4 flex flex-col items-center justify-center">
      {/* Score Card */}
      <div className="pixel-card p-4 mb-4 w-full max-w-xs">
        <div className="flex justify-between border-b-2 border-dirt pb-2 mb-2">
          <span className="retro-body text-sm text-ink">{gameState?.team_a?.name}</span>
          <span className="retro-heading text-lg text-grass">{gameState?.team_a?.score}</span>
        </div>
        <div className="flex justify-between">
          <span className="retro-body text-sm text-ink">{gameState?.team_b?.name}</span>
          <span className="retro-heading text-lg text-grass">{gameState?.team_b?.score}</span>
        </div>
        <div className="text-center mt-3 pt-2 border-t-2 border-dirt">
          <span className="retro-body text-xs text-dirt">
            {isTopInning ? '▲' : '▼'} INN {gameState?.inning} | OUTS: {gameState?.outs}
          </span>
        </div>
      </div>

      {/* Pitching Team Info - Transparent style matching GamePlay.js */}
      <div className="bg-dirt/20 border-4 border-dirt p-4 mb-4 w-full max-w-xs">
        <p className="retro-heading text-lg text-dirt text-center mb-1">
          {pitchingTeam?.name?.toUpperCase()}
        </p>
        <p className="retro-body text-sm text-dirt text-center">
          YOU ARE PITCHING - HOLD THE PHONE
        </p>
      </div>

      {/* Icons - Radio Tower and Baseball */}
      <div className="flex justify-center items-center gap-3 mb-4">
        <RadioTower size={40} animated={true} />
        <Baseball size={50} spinning={false} />
      </div>

      {/* Batting info - Transparent style matching GamePlay.js */}
      <div className="bg-grass/20 border-4 border-grass p-4 mb-4 w-full max-w-xs">
        <p className="retro-body text-xs text-grass text-center mb-1">NOW BATTING</p>
        <p className="retro-heading text-lg text-grass text-center" data-testid="current-batter">
          {currentBatter}
        </p>
        <p className="retro-body text-xs text-grass text-center">({battingTeam?.name})</p>
      </div>

      <p className="retro-body text-sm text-ink mb-3">
        Press PITCH when {currentBatter} is ready!
      </p>

      {/* Pitch button */}
      <button
        onClick={() => { playClick(); sendAction('pitch', {}); }}
        className="pixel-btn pixel-btn-primary text-sm"
        data-testid="pitch-btn"
      >
        PITCH!
      </button>
    </div>
  );
}
